import { AsyncPipe } from '@angular/common';
import type { ElementRef, OnInit } from '@angular/core';
import {
  Component,
  forwardRef,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormValueControl } from '@angular/forms/signals';
import parsePhoneNumberFromString, { AsYouType } from 'libphonenumber-js';
import { map } from 'rxjs';
import type { OnyxOption } from '../../../directives/interfaces';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxDropdownDirective } from '../../../directives/onyx-dropdown.directive';
import { OnyxOverlayPosition } from '../../../enums';
import { DropdownHelper, PhoneHelper } from '../../../helpers';
import type { OnyxPhone } from '../../../interfaces';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { I18N_COUNTRY } from '../../../internal/constants';
import { OnyxFilterPipe } from '../../../pipes/onyx-filter.pipe';
import { OnyxHighlightPipe } from '../../../pipes/onyx-highlight.pipe';
import { OnyxDictionariesService } from '../../../services/onyx-dictionaries.service';
import { OnyxPreferencesService } from '../../../services/onyx-preferences.service';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxFlagComponent } from '../../flags/onyx-flag/onyx-flag.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxClearButtonComponent } from '../../buttons/onyx-clear-button/onyx-clear-button.component';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';

type CountryOption = OnyxOption<string> & { areaCode: string };
type AreaCodeOption = OnyxOption<string> & { countryCode: string };

@Component({
  selector: 'onyx-phone-number-input',
  imports: [
    OnyxInputLabelComponent,
    OnyxClearButtonComponent,
    OnyxDropdownDirective,
    OnyxIconComponent,
    OnyxErrorComponent,
    OnyxFlagComponent,
    OnyxHighlightPipe,
    AsyncPipe,
  ],
  providers: [
    {
      provide: OnyxFilterPipe,
      useClass: OnyxFilterPipe,
    },
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxPhoneNumberInputComponent),
    },
  ],
  templateUrl: './onyx-phone-number-input.component.html',
  styleUrl: './onyx-phone-number-input.component.scss',
})
export class OnyxPhoneNumberInputComponent
  extends OnyxBaseFormControlComponent
  implements FormValueControl<OnyxPhone | null>, OnInit
{
  private readonly dictionaryService = inject(OnyxDictionariesService);
  private readonly preferencesService = inject(OnyxPreferencesService);

  public readonly value = model<OnyxPhone | null>(null);
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly size = input<32 | 40>(40);
  public readonly showErrors = input(true);

  protected readonly OnyxOverlayPosition = OnyxOverlayPosition;

  protected options$ = this.dictionaryService.getDictionary(I18N_COUNTRY).pipe(
    map((options) => DropdownHelper.mapToOptions(options) as CountryOption[]),
    map((options: CountryOption[]): AreaCodeOption[] =>
      options.map((option) => ({
        ...option,
        value: option.areaCode,
        countryCode: option.value,
      })),
    ),
  );

  protected areaCode = signal<string | null>('+48');
  protected countryCode = signal<string | null>(null);
  protected phoneNumber = signal<string | null>(null);
  protected expanded = signal(false);

  protected inputElementRef =
    viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

  public override ngOnInit(): void {
    super.ngOnInit();
    this.areaCode.set(this.preferencesService.defaultAreaCode());

    toObservable(this.value, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.transformValue(value));
  }

  public focus(): void {
    if (this.disabled()) return;
    this.inputElementRef().nativeElement.focus();
  }

  protected setAreaCode(option: AreaCodeOption | null): void {
    this.areaCode.set(option?.value ?? null);
    this.countryCode.set(option?.countryCode ?? null);
    this.setValue();
  }

  protected handleValueChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const sanitizedValue = PhoneHelper.sanitizePhoneNumber(value);

    this.phoneNumber.set(sanitizedValue);
    this.inputElementRef().nativeElement.value = sanitizedValue;
    this.setValue();
  }

  protected pasteNumber(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData?.getData('text') ?? '';
    if (!clipboardData) return;

    const sanitizedData = PhoneHelper.sanitizePhoneNumber(clipboardData);
    const phoneNumber = parsePhoneNumberFromString(sanitizedData);

    if (phoneNumber) {
      const country = phoneNumber.country;
      const callingCode = `+${phoneNumber.countryCallingCode}`;
      const formattedNumber = new AsYouType(country).input(sanitizedData);
      const nationalNumber = PhoneHelper.sanitizePhoneNumber(
        formattedNumber.slice(callingCode.length),
      );

      this.areaCode.set(callingCode);
      this.phoneNumber.set(nationalNumber);
      this.inputElementRef().nativeElement.value = nationalNumber;
    } else {
      this.phoneNumber.set(sanitizedData);
      this.inputElementRef().nativeElement.value = sanitizedData;
    }

    this.setValue();
  }

  protected clearNumber(): void {
    this.phoneNumber.set(null);
    this.inputElementRef().nativeElement.value = '';
    this.setValue();
    this.touch.emit();
  }

  private transformValue(value: OnyxPhone | null): void {
    const defaultAreaCode = this.preferencesService.defaultAreaCode();
    this.areaCode.set(value?.areaCode ?? defaultAreaCode);
    this.phoneNumber.set(value?.number ?? null);
  }

  private setValue(): void {
    const areaCode = this.areaCode();
    const number = this.phoneNumber();
    this.value.set(areaCode && number ? { number, areaCode } : null);
  }
}
